import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { LeadStatus } from '../../types/lead.types';

interface PipelineStage {
  status: LeadStatus;
  label: string;
  count: number;
  value: number;
  color: string;
}

interface PipelineChartProps {
  data: PipelineStage[];
}

export const PipelineChart: React.FC<PipelineChartProps> = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);

  const statusColors: Record<LeadStatus, string> = {
    new: '#3b82f6',
    contacted: '#8b5cf6',
    qualified: '#10b981',
    proposal: '#f59e0b',
    negotiation: '#06b6d4',
    won: '#22c55e',
    lost: '#ef4444',
  };

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      {data.map((stage, index) => {
        const widthPercentage = (stage.value / maxValue) * 100;
        const color = statusColors[stage.status] || '#94a3b8';

        return (
          <Box key={stage.status} sx={{ mb: 3 }}>
            {/* Stage Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {stage.label}
                </Typography>
                <Chip
                  label={stage.count}
                  size="small"
                  sx={{
                    backgroundColor: `${color}20`,
                    color: color,
                    fontWeight: 700,
                    minWidth: 32,
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: color }}>
                ${stage.value.toLocaleString()}
              </Typography>
            </Box>

            {/* Progress Bar */}
            <Box
              sx={{
                width: '100%',
                height: 48,
                backgroundColor: 'rgba(148, 163, 184, 0.1)',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                border: '1px solid rgba(148, 163, 184, 0.2)',
              }}
            >
              <Box
                sx={{
                  width: `${Math.max(widthPercentage, 5)}%`,
                  height: '100%',
                  background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  px: 2,
                  transition: 'all 0.5s ease-in-out',
                  boxShadow: `0 4px 12px ${color}40`,
                  '&:hover': {
                    transform: 'scaleY(1.05)',
                    boxShadow: `0 6px 16px ${color}60`,
                  },
                }}
              >
                {widthPercentage > 15 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    }}
                  >
                    {widthPercentage.toFixed(0)}%
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}

      {/* Summary */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
          border: '1px solid rgba(226, 232, 240, 0.6)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Total Pipeline Value
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
            ${data.reduce((sum, stage) => sum + stage.value, 0).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Total Leads
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'secondary.main' }}>
            {data.reduce((sum, stage) => sum + stage.count, 0)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};